import React, { useState } from 'react';
import ConfigSelector from './ConfigSelector';
import ConfigEditor from './ConfigEditor';
import DeploymentButton from './DeploymentButton';
import StatusDisplay from './StatusDisplay';
import { deploymentConfigs } from '../config/deploymentConfigs';
import awsService from '../services/awsService';

const DeploymentDashboard = () => {
  const [selectedConfig, setSelectedConfig] = useState('');
  const [currentConfig, setCurrentConfig] = useState(null);
  const [configEditorExpanded, setConfigEditorExpanded] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [lambdaResponse, setLambdaResponse] = useState(null);


  const handleConfigChange = (configKey) => {
    setSelectedConfig(configKey);
    setCurrentConfig(deploymentConfigs[configKey] || null);
    setDeploymentStatus(null);
  };

  const handleConfigEdit = (editedConfig) => {
    setCurrentConfig(editedConfig);
  };

  const handleDeploy = async () => {
    if (!selectedConfig || !currentConfig) {
      setDeploymentStatus({
        type: 'error',
        message: 'Please select a configuration first'
      });
      return;
    }

    setIsDeploying(true);
    setDeploymentStatus({
      type: 'loading',
      message: 'Invoking deployment Lambda...'
    });

    // Clear previous response
    setLambdaResponse(null);
    
    try {
      // Trigger deployment with 10-minute timeout
      const result = await triggerDeployment(selectedConfig);
      
      setLambdaResponse(result);
      
      if (result.success) {
        setDeploymentStatus({
          type: 'success',
          message: `Deployment invoked successfully! Request ID: ${result.requestId}`
        });
      } else {
        setDeploymentStatus({
          type: 'error',
          message: `Deployment failed: ${result.error || 'Unknown error'}`
        });
      }

    } catch (error) {
      setDeploymentStatus({
        type: 'error',
        message: `Deployment failed: ${error.message}`
      });
      setLambdaResponse({
        success: false,
        error: error.message
      });
    } finally {
      setIsDeploying(false);
    }
  };

  const triggerDeployment = async (configKey) => {
    const config = currentConfig || deploymentConfigs[configKey];
    
    try {
      console.log('Invoking deployment Lambda with config:', config);
      const result = await awsService.invokeDeploymentLambda(config, 'prod');
      
      if (result.success) {
        console.log('Deployment Lambda invoked successfully:', result.requestId);
        return result;
      } else {
        throw new Error(`Lambda invocation failed: Status ${result.statusCode}`);
      }
    } catch (error) {
      console.error('Deployment error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  return (
    <div className="card">
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ 
          color: '#2c3e50', 
          fontSize: '2.5rem', 
          marginBottom: '10px' 
        }}>
          CodeDeploy Dashboard
        </h1>
        <p style={{ 
          color: '#7f8c8d', 
          fontSize: '1.1rem' 
        }}>
          Deploy your services with confidence
        </p>
      </div>

      <ConfigSelector
        selectedConfig={selectedConfig}
        onConfigChange={handleConfigChange}
        configs={deploymentConfigs}
      />

      {currentConfig && (
        <ConfigEditor
          config={currentConfig}
          onConfigChange={handleConfigEdit}
          isExpanded={configEditorExpanded}
          onToggleExpanded={() => setConfigEditorExpanded(!configEditorExpanded)}
        />
      )}

      <DeploymentButton
        selectedConfig={selectedConfig}
        isDeploying={isDeploying}
        onDeploy={handleDeploy}
      />

      <StatusDisplay status={deploymentStatus} />

      {lambdaResponse && (
        <div style={{
          background: '#f8f9fa',
          borderRadius: '8px',
          padding: '20px',
          marginTop: '20px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ 
            color: '#2c3e50', 
            marginBottom: '15px',
            fontSize: '1.2rem'
          }}>
            Lambda Response
          </h3>
          <pre style={{
            background: '#ffffff',
            padding: '15px',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            fontSize: '0.9rem',
            overflowX: 'auto',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {JSON.stringify(lambdaResponse, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default DeploymentDashboard;